import React, { useMemo } from 'react';
import moment from 'moment';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Table from '../../../../components/Table';
import TableDateCell from '../../../../components/Table/TableDateCell';
import TableUserInfoCell from '../../../../components/Table/TableUserInfoCell';
import GeneralFormItem from '../../../../components/GeneralFormItem';
import { queryTypeConfigLookup } from '../../../../utils/queryTypeLookup';
import { assertCondition } from '../../../../utils/types';
import { recentActivityTableConfig } from './tableStylesConfig';
import { IRecentActivity } from './types';
import EmptyRecentActivity from './EmptyRecentActivity';
import RecentActivityTableDisplay from './RecentActivityTableDisplay';

interface IProps {
  recentActivity: IRecentActivity[];
}

const columnHelper = createColumnHelper<IRecentActivity>();

const RecentActivityTable = ({ recentActivity }: IProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('display', {
        id: 'title',
        header: 'Title',
        meta: recentActivityTableConfig.display,
        cell: (info) => {
          const row = info.row.original;

          return <RecentActivityTableDisplay recentActivity={row} />;
        },
      }),
      columnHelper.accessor('__typename', {
        id: 'type',
        header: 'Type',
        meta: recentActivityTableConfig.queryType,
        cell: (info) => {
          const type = info.getValue();
          const row = info.row.original;

          if (type === 'RecentDashboardVisit') {
            return <GeneralFormItem label={'Dashboard'} />;
          }

          assertCondition(row.__typename === 'RecentAnalysisVisit');

          const { display } = queryTypeConfigLookup[row.queryType];

          return <GeneralFormItem label={display} />;
        },
      }),
      columnHelper.accessor('visitedAt', {
        id: 'visitedAt',
        header: 'Last activity',
        meta: recentActivityTableConfig.visitedAt,
        cell: (info) => {
          const visitedAt = info.getValue();

          const label = `Visited ${moment.utc(visitedAt).fromNow()}`;

          return <GeneralFormItem label={label} />;
        },
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Created',
        meta: recentActivityTableConfig.createdAt,
        cell: (info) => {
          const createdAt = info.getValue();

          if (!createdAt) {
            return null;
          }

          return <TableDateCell date={createdAt} />;
        },
      }),
      columnHelper.accessor('creator', {
        id: 'creator',
        header: 'Creator',
        meta: recentActivityTableConfig.creator,
        cell: (info) => {
          const creator = info.getValue();

          return (
            <TableUserInfoCell picture={creator.picture} name={creator.name} />
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: recentActivity,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table
      headerGroups={table.getHeaderGroups()}
      rows={table.getRowModel().rows}
      noContentComponent={<EmptyRecentActivity />}
    />
  );
};

export default RecentActivityTable;
